import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import {
  NextimeShippingResponse,
  NextimeDeliveryDate,
} from "../models/nextime-response.interface";
import {
  NextimeSite,
  NextimeSitePartialUpdate,
} from "../models/nextime-site.interface";
import { NextimeRequestPayload } from "../models/nextime-request.interface";
import { ScheduledOrderShippingRateOption } from "../models/scheduled-order-shipping-rate-option.interface";

/**
 * Service for interacting with Nextime delivery date API
 * Handles fetching shipping options, delivery dates, and site settings
 */
@Injectable({
  providedIn: "root",
})
export class NextimeService {
  private nextimeUrl: string = environment.nextimeApiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get shipping options with delivery dates from Nextime
   * @param siteId Nextime site ID
   * @param apiKey Nextime API key
   * @param payload Request payload with order details
   * @returns Observable of Nextime shipping response
   */
  getShippingOptions(
    siteId: string,
    apiKey: string,
    payload: NextimeRequestPayload,
  ): Observable<NextimeShippingResponse> {
    const endpoint = `${this.nextimeUrl}/${siteId}/shipping-options`;

    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    if (apiKey) {
      headers = headers.append("nextime-apikey", apiKey);
    }

    return this.http.post<NextimeShippingResponse>(endpoint, payload, {
      headers,
    });
  }

  /**
   * Convert Nextime delivery dates to QPilot shipping rate options
   * @param deliveryDates Array of Nextime delivery dates
   * @param preferredShippingRate Optional preferred shipping rate to mark as default
   * @returns Array of QPilot shipping rate options
   */
  convertToQPilotOptions(
    deliveryDates: NextimeDeliveryDate[],
    preferredShippingRate?: string | null,
  ): ScheduledOrderShippingRateOption[] {
    const result: ScheduledOrderShippingRateOption[] = [];

    for (const date of deliveryDates) {
      for (const line of date.shippingLines) {
        const isPreferred =
          preferredShippingRate != null &&
          line.shippingMethod === preferredShippingRate;

        result.push({
          OptionValue: line.shippingMethod,
          OptionText: `${line.name} - $${line.total} - ${line.shippingMethod} - ${date.deliveryDate}`,
          IsDefault: isPreferred,
        });
      }
    }

    return result;
  }

  /**
   * Get Nextime site settings
   * @param siteId Nextime site ID
   * @returns Observable of Nextime site settings
   */
  getSite(siteId: number): Observable<NextimeSite> {
    const endpoint = `${this.nextimeUrl}/sites/${siteId}`;
    const headers = new HttpHeaders({
      Accept: "application/json",
    });

    return this.http.get<NextimeSite>(endpoint, { headers });
  }

  /**
   * Partially update Nextime site settings
   * @param siteId Nextime site ID
   * @param partialUpdate Partial site update data
   * @returns Observable of updated Nextime site
   */
  partialUpdateSite(
    siteId: number,
    partialUpdate: NextimeSitePartialUpdate,
  ): Observable<NextimeSite> {
    const endpoint = `${this.nextimeUrl}/sites/${siteId}`;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    return this.http.patch<NextimeSite>(endpoint, partialUpdate, { headers });
  }
}
